import { invoiceLocaleToLanguage } from '@features/invoices/constants/invoiceLocaleToLanguage'
import { InvoiceDiscountLocation } from '@features/invoices/enums/invoiceDiscountLocation'
import { InvoiceDiscountVat } from '@features/invoices/enums/invoiceDiscountVat'
import { InvoiceForm } from '@features/invoices/types/invoiceForm/invoiceForm'
import { InvoiceCustomer } from '@features/invoices/types/invoices/invoiceCustomer'
import { InvoiceDiscount } from '@features/invoices/types/invoices/invoiceDiscount'
import { InvoiceLine } from '@features/invoices/types/invoices/invoiceLine'
import { Quote } from '@features/invoices/types/quotes/quote'
import { filterDisplayedPaymentMethods } from '@features/invoices/utils/filterDisplayedPaymentMethods'
import { hasInvoiceLineDiscount } from '@features/invoices/utils/hasInvoiceLineDiscount'
import { getLinesPayload } from '@features/invoices/utils/invoicePayload/getLinesPayload'
import { sanitizeStringField } from '@features/invoices/utils/sanitizeStringField'
import { PaymentMethod } from '@features/invoicingSettings/types/paymentMethod'

import { getUpdatedQuoteSummary } from './getUpdatedQuoteSummary'
import { getZervantContactType } from './getZervantContactType'

// The document-level discount only carries an amount in Global (perTotal) mode -
// PerLine discounts live on each line's own `discount` field (see getQuoteLines).
// Returns undefined when there's no discount to set or clear, so quotes that never
// had one don't get a synthetic zero discount written on every save.
const getDiscountOverride = (formData: InvoiceForm, current?: InvoiceDiscount): InvoiceDiscount | undefined => {
  const hasLineDiscount = hasInvoiceLineDiscount(formData.invoiceLines)
  const isGlobalDiscount = !hasLineDiscount && formData.discount > 0

  if (!hasLineDiscount && !isGlobalDiscount && !current) {
    return undefined
  }

  return {
    amount: isGlobalDiscount ? formData.discount : 0,
    location: isGlobalDiscount ? InvoiceDiscountLocation.PerTotal : InvoiceDiscountLocation.PerLine,
    type: formData.discountType,
    vat: InvoiceDiscountVat.PreVat, // discounts are calculated preVat only
  }
}

// getLinesPayload produces the draft-payload line union (InvoicePayloadLine[]).
// The /v1/estimates PUT validates against the estimate response schema (InvoiceLine[]),
// which is structurally compatible — verified end-to-end against the BE. The cast is
// isolated here so any future divergence surfaces in one place.
const getQuoteLines = (formData: InvoiceForm): InvoiceLine[] => getLinesPayload(formData) as unknown as InvoiceLine[]

// We don't reuse getReceiverPayload because it emits InvoicePayloadContact (the
// draft-payload shape), whereas Quote.customer is InvoiceCustomer (the GET-response
// shape) and carries server-only fields like customerId/firstName/lastName that the
// form's receiver doesn't track. Deep-merging over `current.customer` preserves them.
const getCustomerOverride = (formData: InvoiceForm, current: InvoiceCustomer): InvoiceCustomer => {
  if (!formData.receiver) {
    return current
  }

  const {
    addressLine2,
    cityText,
    contactNo,
    countryId,
    ean,
    eInvoiceScheme,
    email,
    name,
    paymentTermsDays,
    phone,
    publicEntityRoutingEnabled,
    registrationNo,
    street,
    type,
    vatNo,
    zipcodeText,
  } = formData.receiver

  return {
    ...current,
    address: {
      ...current.address,
      city: cityText || null,
      country: countryId || null,
      pobox: zipcodeText || null,
      streetAddress1: street || null,
      streetAddress2: addressLine2 ?? null,
    },
    businessId: registrationNo || null,
    companyName: name || null,
    customerNumber: Number(contactNo) || current.customerNumber,
    customerType: getZervantContactType(type),
    displayName: name || current.displayName,
    eInvoiceAddress: {
      ...(current.eInvoiceAddress || { networkOperatorId: null, networkType: null }),
      address: ean || '',
      scheme: eInvoiceScheme || '',
    },
    email: email || null,
    isPublicEntity: publicEntityRoutingEnabled ?? current.isPublicEntity,
    paymentTerm: paymentTermsDays ?? current.paymentTerm,
    phoneNumberWork: phone || null,
    vatNumber: vatNo || null,
  }
}

// Text fields are sanitized via the shared sanitizeStringField helper.
export const getApprovedQuoteOverrides = (
  formData: InvoiceForm,
  current: Quote,
  configuredPaymentMethods: PaymentMethod[],
): Partial<Quote> => {
  const language = invoiceLocaleToLanguage[formData.language] ?? current.language
  const discount = getDiscountOverride(formData, current.discount)

  return {
    buyerReference: formData.buyerReference ?? null,
    customer: getCustomerOverride(formData, current.customer),
    description: sanitizeStringField(formData.message),
    ...(discount && { discount }),
    dueDate: formData.dueDate ? formData.dueDate.toISOString() : null,
    footerText: sanitizeStringField(formData.footerNote),
    issueDate: formData.issueDate.toISOString(),
    language,
    lines: getQuoteLines(formData),
    locale: formData.language,
    paymentMeans: filterDisplayedPaymentMethods(configuredPaymentMethods, formData.displayedPaymentMethods),
    paymentTime: formData.paymentTerm,
    purchaseOrderNumber: formData.purchaseOrderNumber ?? null,
    summary: getUpdatedQuoteSummary(formData, current.summary),
  }
}
